import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Lock, ShieldCheck } from "lucide-react";

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export default function Checkout() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/orders/mine").then((r) => {
      const o = r.data.find((x) => x.order_id === orderId);
      if (!o) { toast.error("Order not found"); nav("/pricing"); }
      else setOrder(o);
    }).catch(() => nav("/login"));
  }, [orderId, nav]);

  const pay = async () => {
    setBusy(true);
    try {
      // Ask backend to start a Razorpay order (returns razorpay_order_id + key OR falls back to mock mode)
      const { data: init } = await api.post(`/checkout/razorpay/init/${orderId}`);
      if (init.mode === "mock") {
        // No Razorpay keys yet — confirm as mock
        await api.post(`/checkout/confirm/${orderId}`);
        toast.success("Welcome to SV Talent Agency. Your profile is ready.");
        nav("/onboarding");
        return;
      }
      // Live/test Razorpay flow
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error("Razorpay SDK failed to load");
      const rzp = new window.Razorpay({
        key: init.key_id,
        amount: init.amount, // paise
        currency: "INR",
        name: "SV Talent Agency",
        description: order.plan_name,
        order_id: init.razorpay_order_id,
        theme: { color: "#C5A059" },
        handler: async (response) => {
          try {
            await api.post(`/checkout/razorpay/verify`, {
              order_id: orderId,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success("Payment confirmed. Welcome to SV Talent.");
            nav("/onboarding");
          } catch {
            toast.error("Payment verification failed. Contact support.");
          }
        },
        modal: { ondismiss: () => setBusy(false) },
      });
      rzp.open();
    } catch (e) {
      toast.error(e.message || "Payment could not start.");
    } finally { setBusy(false); }
  };

  if (!order) return <div className="min-h-screen"><Header /></div>;

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-4xl mx-auto px-4 py-16">
        <div className="sv-overline mb-4">Checkout</div>
        <h1 className="font-serif-hero text-4xl lg:text-5xl text-[#F4E4BC]">Confirm your membership.</h1>

        <div className="mt-10 grid md:grid-cols-5 gap-8">
          <div className="md:col-span-3 sv-card p-8">
            <div className="font-mono-label text-[10px] text-[#C5A059] mb-4">Order Summary</div>
            <div className="flex justify-between py-3 border-b border-[rgba(255,255,255,0.06)] text-[#CDC7BA]">
              <div>{order.plan_name}</div><div>₹{order.amount}</div>
            </div>
            <div className="flex justify-between py-3 border-b border-[rgba(255,255,255,0.06)] text-[#CDC7BA]">
              <div>GST (18%)</div><div>₹{order.gst}</div>
            </div>
            <div className="flex justify-between py-4 font-serif-editorial text-2xl text-[#F4E4BC]">
              <div>Total</div><div>₹{order.total}</div>
            </div>
            <div className="mt-6 text-xs text-[#8A8478] flex items-start gap-2 leading-relaxed">
              <Lock size={12} className="mt-0.5 flex-shrink-0" />
              <span>Payments are processed securely via Razorpay. If Razorpay keys are not yet configured, this checkout will run in Test/Mock Mode and no money will be charged.</span>
            </div>
          </div>
          <div className="md:col-span-2 space-y-4">
            <div className="sv-card p-6">
              <div className="font-mono-label text-[10px] text-[#C5A059] mb-2">Billing</div>
              <div className="text-[#CDC7BA]">One-time application fee</div>
            </div>
            <div className="sv-card p-6 space-y-2 text-sm text-[#CDC7BA]">
              <div className="font-mono-label text-[10px] text-[#C5A059] mb-2">Included</div>
              <div className="flex items-center gap-2"><ShieldCheck size={14} className="text-[#C5A059]" /> Privacy protected</div>
              <div className="flex items-center gap-2"><ShieldCheck size={14} className="text-[#C5A059]" /> Secure payment</div>
              <div className="flex items-center gap-2"><ShieldCheck size={14} className="text-[#C5A059]" /> Transparent pricing</div>
            </div>
            <button data-testid="checkout-confirm" disabled={busy} onClick={pay} className="sv-btn-primary w-full">
              {busy ? "Processing…" : `Pay ₹${order.total}`}
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
